import { TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { TeamAvatar } from '@sorare/core/src/components/club/TeamAvatar';
import { PlayerGameScoreScore } from '@sorare/core/src/components/scoring/PlayerGameScoreScore';
import { isType } from '@sorare/core/src/gql';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';

import { LastNBAGame } from './LastNBAGame';
import { RecommendationLastGame_anyPlayer } from './__generated__/index.graphql';

const Wrapper = styled(Horizontal).attrs({ gap: 1, center: true })``;

type Props = { player: RecommendationLastGame_anyPlayer };

export const LastGame = ({ player }: Props) => {
  const {
    flags: { useUsRailsBackend = false },
  } = useFeatureFlags();

  const lastGame = player.playerGameScores?.[0];
  if (!lastGame) {
    return null;
  }
  const { anyGame } = lastGame;
  const { awayTeam, homeTeam } = anyGame;

  //TODO: DEPRECATE WHEN US SPORTS ARE MIGRATED TO RAILS
  // linear https://linear.app/sorare/issue/MAR-715/clean-for-you-recommendation-components-lastfifteenand-lastgamewhen

  if (isType(player, 'NBAPlayer') && !useUsRailsBackend)
    return <LastNBAGame player={player} />;

  const playsAgainst =
    awayTeam?.slug &&
    [player.activeClub?.slug, player.activeNationalTeam?.slug].includes(
      awayTeam.slug
    )
      ? homeTeam
      : awayTeam;

  if (!playsAgainst) {
    return null;
  }
  return (
    <Wrapper>
      <PlayerGameScoreScore playerGameScore={lastGame} />
      vs
      <TeamAvatar withTooltip size={24} team={playsAgainst} />
    </Wrapper>
  );
};
LastGame.fragments = {
  anyPlayer: gql`
    fragment RecommendationLastGame_anyPlayer on AnyPlayerInterface {
      slug
      activeClub {
        slug
      }
      activeNationalTeam {
        slug
      }
      displayName
      playerGameScores(last: 1) {
        id
        score
        anyGame {
          id
          awayTeam {
            slug
            ...TeamAvatar_team
          }
          homeTeam {
            slug
            ...TeamAvatar_team
          }
        }
        ...PlayerGameScoreScore_playerGameScoreInterface
      }
      ... on NBAPlayer {
        slug
        ...LastNBAGame_nbaPlayer
      }
    }
    ${LastNBAGame.fragments.nbaPlayer}
    ${TeamAvatar.fragments.team}
    ${PlayerGameScoreScore.fragments.playerGameScoreInterface}
  ` as TypedDocumentNode<RecommendationLastGame_anyPlayer>,
};
